import React, { useState, useEffect, useContext } from 'react';
import _ from 'underscore';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {
  FluentProvider,
  webLightTheme,
  RadioGroup,
  Radio,
} from '@fluentui/react-components';
import { ValidatedBudgetInput } from '@bingads-webui-campaign-react/budget-component-v2';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { BudgetItemLabel, CustomBudgetLabel } from './budget-item';
import { BudgetSuggestionTiers, getMatchingBudgetTierBasedOnBudget, CustomBudget } from '../model/constants';

const getStyles = () => ({
  selectedRadio: {
    border: '1px solid rgba(15, 108, 189, 1)',
    padding: '12px, 16px, 12px, 8px',
    borderRadius: '4px',
  },
  radio: {
    width: '100%',
    '& .fui-Label': {
      width: '100%',
    },
  },
  radioGroup: {
    width: '100%',
  },
});

export const BudgetSuggestionContainer = ({
  data,
  isLoading,
  mode,
  campaignType,
  currency,
  onBudgetValidate,
  customPerformanceData,
  defaultBudget,
  bidType,
  minBudget,
  maxBudget,
  showDropdown,
  onSelectCurrency,
  currencies,
}) => {
  const classes = useDefaultStyles(getStyles);

  const [selectedBudget, setSelectedBudget] = useState(null);

  const [customBudgetResult, setCustomBudgetResult] = useState(null);

  const i18n = useContext(I18nContext);

  const hasError = !isLoading && _.isEmpty(data);

  useEffect(() => {
    if (!isLoading && !hasError) {
      if (selectedBudget && selectedBudget !== CustomBudget) {
        const BudgetAmount = data && data[selectedBudget] && data[selectedBudget].BudgetAmountDaily;

        onBudgetValidate({ data: { budget: BudgetAmount, budgetTier: selectedBudget }, isValid: true, messages: [] });
      } else if (customBudgetResult) {
        onBudgetValidate({ data: { ...customBudgetResult.data, budgetTier: selectedBudget }, isValid: customBudgetResult.isValid, messages: customBudgetResult.messages });
      }
    }

    if (hasError && customBudgetResult) {
      onBudgetValidate({ data: { ...customBudgetResult.data, budgetTier: selectedBudget }, isValid: customBudgetResult.isValid, messages: customBudgetResult.messages });
    }
  }, [customBudgetResult, hasError, data, isLoading, onBudgetValidate, selectedBudget]);


  useEffect(() => {
    const matchingTier = getMatchingBudgetTierBasedOnBudget(data, defaultBudget, mode);

    if (selectedBudget === null && !_.isEmpty(data) && defaultBudget) {
      setSelectedBudget(matchingTier);
    }
  }, [defaultBudget, data, selectedBudget, mode]);

  if (hasError) {
    return (
      <>
        <ValidatedBudgetInput
          validationProps={{
            onValidate: setCustomBudgetResult,
            data: (customBudgetResult && customBudgetResult.data) || { budget: defaultBudget },
            initialValue: defaultBudget,
            alwaysShowError: (customBudgetResult && customBudgetResult.isValid === false),
          }}
          budgetKey="budget"
          currency={currency || 'USD'}
          label={i18n.getString(_TL_('Set the campaign\'s average daily budget'))}
          className="custom-budget-input"
          minBudget={minBudget}
          maxBudget={maxBudget}
          showDropdown={showDropdown}
          onSelectCurrency={onSelectCurrency}
          currencies={currencies}
        />
      </>
    );
  }

  return (
    <FluentProvider theme={webLightTheme}>
      <div style={{ marginBottom: 5, marginTop: 5 }}>
        {i18n.getString(_TL_('The following budget options are based on performance estimates from businesses with similar campaigns, ad settings, and budgets.'))}
      </div>
      <RadioGroup defaultValue={selectedBudget} className={classNames('budget-suggestion-group', classes.radioGroup, isLoading ? 'budget-loading' : '')} label="Choose an option" onChange={(e, val) => setSelectedBudget(val.value)}>
        {(
          Object.keys(BudgetSuggestionTiers).map(tier => (
            <Radio
              key={tier}
              value={tier}
              label={<BudgetItemLabel
                tier={tier}
                data={data && data[tier]}
                isSelected={selectedBudget === tier}
                isLoading={isLoading}
                mode={mode}
                currency={currency}
                campaignType={campaignType}
                bidType={bidType}
              />}
              checked={selectedBudget === tier}
              className={selectedBudget === tier ?
                classNames(classes.selectedRadio, classes.radio, `budget-radio-${tier}`, 'selected')
                : classNames(`budget-radio-${tier}`, classes.radio)}
            />
          ))
        )
        }
        <Radio
          key="CustomBudget"
          value="CustomBudget"
          label={<CustomBudgetLabel
            isSelected={selectedBudget === CustomBudget}
            isLoading={isLoading}
            campaignType={campaignType}
            currency={currency}
            onUpdateCustomBudget={setCustomBudgetResult}
            customBudgetResult={customBudgetResult}
            customPerformanceData={customPerformanceData}
            defaultBudget={defaultBudget}
            bidType={bidType}
            minBudget={minBudget}
            maxBudget={maxBudget}
            showDropdown={showDropdown}
            onSelectCurrency={onSelectCurrency}
            currencies={currencies}
          />}
          checked={selectedBudget === CustomBudget}
          className={selectedBudget === CustomBudget ?
            classNames(classes.selectedRadio, classes.radio, `budget-radio-${CustomBudget}`, 'selected')
            : classNames(`budget-radio-${CustomBudget}`, classes.radio)}
        />
      </RadioGroup>
    </FluentProvider>
  );
};

BudgetSuggestionContainer.propTypes = {
  data: PropTypes.shape({}).isRequired,
  isLoading: PropTypes.bool.isRequired,
  mode: PropTypes.string.isRequired,
  campaignType: PropTypes.string.isRequired,
  currency: PropTypes.string.isRequired,
  onBudgetValidate: PropTypes.func.isRequired,
  customPerformanceData: PropTypes.shape({}),
  defaultBudget: PropTypes.number.isRequired,
  showDropdown: PropTypes.bool,
  onSelectCurrency: PropTypes.func,
  currencies: PropTypes.arrayOf(PropTypes.shape({})),
};

BudgetSuggestionContainer.defaultProps = {
  customPerformanceData: null,
  showDropdown: false,
  onSelectCurrency: _.noop,
  currencies: [],
};
